import { BigNumber } from "bignumber.js";
import { truncateDecimal } from "../web3/balanceUtils";
import {
  approval,
  concludeCrossChain,
  concludeSameChain,
  erc20Deposit,
  erc20Withdrawal,
  ethDeposit,
  ethWithdrawal,
  fund,
  close,
  withdraw,
} from "../constants/Constants";
import EthereumAsset from "../api/types/ethereumAsset";
import StellarAsset from "../api/types/stellarAsset";
import { NervosAsset } from "../api/types/nervosAsset";

const bn = BigNumber!;

// calculateTransactionFee adds all transaction fees up for each chain and returns them in the correct currency using the quotes.
export function calculateTransactionFee(
  assets: Map<string, EthereumAsset | StellarAsset | NervosAsset>,
  fromAssetCode: string,
  toAssetCode: string,
  crossQuote: number,
  fromQuote: number,
  toQuote: number,
  fromGas: string,
  toGas: string,
): {
  fromTxFee: string;
  toTxFee: string;
  exchangeRate: number;
} {
  let fromTxFee = "0.0000";
  let toTxFee = "0.0000";
  // For Cross-Chain swaps we conclude on both chains, so concludeFinal is set to concludeCrossChain.
  // For Same-Chain swaps we conclude only once, so we set concludeFinal for one asset to 0 and one asset to concludeSameChain.
  // isFromAssetERC20, isToAssetERC20, isFromAssetETH, isToAssetETH are used to check the asset types.
  const fromAsset = assets.get(fromAssetCode!)!,
    toAsset = assets.get(toAssetCode!)!,
    isSameChain = fromAsset.chainID === toAsset.chainID,
    isFromAssetERC20 = fromAsset.type === "ERC20",
    isToAssetERC20 = toAsset.type === "ERC20",
    isFromAssetETH = fromAsset.type === "ETH",
    isToAssetETH = toAsset.type === "ETH",
    isFromAssetSte = fromAsset instanceof StellarAsset,
    isToAssetSte = toAsset instanceof StellarAsset,
    concludeGasUsedETHFrom = isSameChain
      ? isFromAssetETH
        ? concludeSameChain
        : 0
      : concludeCrossChain,
    concludeGasUsedETHTo = isSameChain
      ? isFromAssetETH
        ? 0
        : concludeSameChain
      : concludeCrossChain,
    concludeGasUsedERC20From = isSameChain
      ? isFromAssetETH
        ? concludeSameChain
        : 0
      : concludeCrossChain,
    concludeGasUsedERC20To = isSameChain
      ? isFromAssetETH
        ? 0
        : concludeSameChain
      : concludeCrossChain;

  const fromGasParsed = new bn(fromGas as string).div(new bn(10).pow(18));
  const toGasParsed = new bn(toGas as string).div(new bn(10).pow(18));
  const fundParsed = new bn(fund).div(10000000);
  const closeParsed = new bn(close).div(10000000);
  const withdrawParsed = new bn(withdraw).div(10000000);

  if (isFromAssetERC20) {
    fromTxFee = fromGasParsed
      .times(new bn(erc20Withdrawal).plus(new bn(concludeGasUsedERC20From)))
      .times(fromQuote)
      .toString();
  } else if (isFromAssetETH) {
    fromTxFee = fromGasParsed
      .times(new bn(ethWithdrawal).plus(new bn(concludeGasUsedETHFrom)))
      .toString();
  } else if (isFromAssetSte) {
    if (isToAssetSte) {
      fromTxFee = fundParsed
        .plus(withdrawParsed.times(2))
        .times(fromQuote)
        .toString();
    } else {
      fromTxFee = closeParsed.plus(withdrawParsed).times(fromQuote).toString();
    }
  }

  if (isToAssetERC20) {
    const gas = toGasParsed
      .times(
        new bn(erc20Withdrawal)
          .plus(new bn(concludeGasUsedERC20To))
          .plus(new bn(erc20Deposit))
          .plus(new bn(approval)),
      )
      .times(toQuote);
    toTxFee = gas.toString();
  } else if (isToAssetETH) {
    const gas = toGasParsed.times(
      new bn(ethWithdrawal)
        .plus(new bn(ethDeposit))
        .plus(new bn(concludeGasUsedETHTo)),
    );
    toTxFee = gas.toString();
  } else if (isToAssetSte) {
    if (isFromAssetSte) {
      toTxFee = "0.0";
    } else {
      toTxFee = fundParsed
        .plus(closeParsed.plus(withdrawParsed))
        .times(toQuote)
        .toString();
    }
  }

  return { fromTxFee: fromTxFee, toTxFee: toTxFee, exchangeRate: crossQuote };
}

// updateFromAmount gets the input on the user's chain and returns the input and swapAmounts.
export function updateFromAmount(
  amount: string,
  exchangeRate: number,
  fromGas: string,
  toGas: string,
  fromAssetPrecision: number,
  toAssetPrecision: number,
): { fromAmount: string; toAmount: string; toInput: string } {
  const amountNum = parseFloat(amount);
  if (isNaN(amountNum)) {
    return { fromAmount: "", toAmount: "", toInput: "" };
  }

  // fromAmount = input + transaction fee on user's chain.
  const fromAmount = truncateDecimal(
    new bn(amountNum).plus(new bn(fromGas as string)).toString(),
    fromAssetPrecision,
    "UP",
  );

  // balInput = input * rate
  const balInput = Number(bn(amountNum).times(exchangeRate)).toString();

  // truncBalAmount = input * rate - transaction fees on hub's chain.
  const truncBalAmount = truncateDecimal(
    Number(
      new bn(amountNum).times(exchangeRate).minus(new bn(toGas as string)),
    ).toString(),
    toAssetPrecision,
    "DOWN",
  );
  return {
    fromAmount: fromAmount.toString(),
    toAmount: truncBalAmount,
    toInput: balInput,
  };
}

// updateToAmount gets the input on the hub's chain and returns the input and swapAmounts.
export function updateToAmount(
  amount: string,
  exchangeRate: number,
  fromGas: string,
  toGas: string,
  fromAssetPrecision: number,
  toAssetPrecision: number,
): { fromAmount: string; toAmount: string; fromInput: string } {
  const amountNum = parseFloat(amount);
  if (isNaN(amountNum)) {
    return { fromAmount: "", toAmount: "", fromInput: "" };
  }

  // toAmount = input - transaction fees on hub's chain.
  const toAmount = truncateDecimal(
    new bn(amountNum).minus(new bn(toGas as string)).toString(),
    toAssetPrecision,
    "DOWN",
  );

  // truncBalAmount = toAmount / rate + transaction fees on user's chain.
  const truncBalAmount = truncateDecimal(
    Number(
      new bn(toAmount).div(exchangeRate).plus(new bn(fromGas as string)),
    ).toString(),
    fromAssetPrecision,
    "UP",
  );

  // balInput = input / rate.
  const balInput = Number(new bn(amountNum).div(exchangeRate)).toString();
  return {
    fromAmount: truncBalAmount,
    toAmount: toAmount.toString(),
    fromInput: balInput,
  };
}

// calculateMinAmount calculates the minimum swap amount: input = transaction fees on hub's chain / rate.
export function calculateMinAmount(
  exchangeRate: number,
  toTXFee: string,
  precision: number,
): string {
  const min = truncateDecimal(
    new bn(toTXFee as string).div(exchangeRate).toString(),
    precision,
    "UP",
  );
  if (parseFloat(min) < 0) {
    return "0.0";
  }
  return min;
}
